import { hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import App, { createQueryClient } from './App';
import { initI18n, namespacesForPath } from './i18n/config';
import { preloadRoute } from '#route-pages';
import { AtlasCatalogContext } from './hooks/atlasCatalogContext';
import type { AtlasCatalog } from './types/atlas';
import { setLivePlanNames } from './config/planNames';
import './index.css';
import './styles/mobile-gutters.css';

declare global {
  interface Window {
    __INITIAL_I18N__?: {
      initialLanguage: string;
    };
  }
}

function catalogFromPage(): AtlasCatalog | undefined {
  const node = document.getElementById('bidmio-atlas-catalog');
  if (!node?.textContent) return undefined;
  try {
    return JSON.parse(node.textContent) as AtlasCatalog;
  } catch {
    return undefined;
  }
}

function RootApp({ queryClient }: { queryClient: ReturnType<typeof createQueryClient> }) {
  return (
    <AtlasCatalogContext.Provider value={catalogFromPage()}>
      <App queryClient={queryClient} />
    </AtlasCatalogContext.Provider>
  );
}

/** Plan names the server rendered with, so hydration matches its HTML. */
function planNamesFromPage(): void {
  const node = document.getElementById('bidmio-plan-names');
  if (!node?.textContent) return;
  try {
    setLivePlanNames(JSON.parse(node.textContent));
  } catch {
    // Bundled names stay in place.
  }
}

async function hydrate() {
  planNamesFromPage();
  const initialLanguage = typeof window !== 'undefined' ? window.__INITIAL_I18N__?.initialLanguage : undefined;
  const pathname = window.location.pathname;
  const i18n = await initI18n(initialLanguage, namespacesForPath(pathname));
  await preloadRoute(pathname);
  const queryClient = createQueryClient();

  hydrateRoot(
    document.getElementById('root')!,
    <I18nextProvider i18n={i18n}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <RootApp queryClient={queryClient} />
      </BrowserRouter>
    </I18nextProvider>
  );
}

void hydrate();
